import axios, { type AxiosInstance } from 'axios';
import qs, { type IStringifyOptions } from 'qs';
import { BASE_REST_API_URL } from 'common/constants';

export abstract class BaseApiClient {
  /**
   * Temporary solution to sync JWT token between Recoil and API client services created by singleton factories.
   * Can be removed when all singleton factories for API client services are replaced by
   * creation in `ServicesProvider` and obtained by `useServices` hook.
   */
  public static authToken: string | undefined;

  /**
   * @param prefix Entity prefix for API requests, eg. `users`, `posts`, `comments`, etc.
   * @param backendURL Was meant to be used for testing purposes. Set to `undefined` to use default value.
   * @param authToken Should be propagated from child class constructor if child instance is created
   * in `ServicesProvider`. Look at `AuthApiClient` file for more details.
   * Can be set to `false` to disable authorization header.
   */
  constructor(
    protected prefix: string,
    protected backendURL?: string,
    protected authToken?: false | string,
    protected readonly stringifyOptions?: IStringifyOptions,
  ) {}

  protected get axios(): AxiosInstance {
    const authToken = this.authToken ?? BaseApiClient.authToken;

    return axios.create({
      baseURL: `${this.backendURL ?? BASE_REST_API_URL}/${this.prefix}`,
      headers: {
        ...(authToken === false ? {} : { Authorization: `Bearer ${authToken}` }),
      },
      paramsSerializer: {
        serialize: params => (typeof params === 'string' ? params : qs.stringify(params, this.stringifyOptions)),
      },
    });
  }

  protected getSignedUrl(path: string): URL {
    const url = new URL(`${this.axios.defaults.baseURL}/${path}`);
    const authToken = this.authToken ?? BaseApiClient.authToken;

    if (authToken) {
      url.searchParams.append('token', authToken);
    }

    return url;
  }
}
